import './style.scss'
import { useContext, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import AppInfoContext from "src/provider/state-manager/appInfoProvider"
import ApiContext from "src/provider/API/call-service"
import utility from "src/utils/utility"
import { AbsoluteContainer, AppSpan, Button, CustomContainer, FlexRow, Form, FormGroup, PageTitle, PageWrapper } from "src/style"
import * as device from 'react-device-detect'
import helpers from "src/utils/helpers"
import { 
    categoriesSettingColumns, productsSettingColumns, 
    categoriesSettingData, productsSettingData 
} from 'src/utils/table-layout'
import { Filter, ServerSideTable, Table } from "src/component"

const Settings = () => {
    const navigate = useNavigate()
    const {info: {selectedSettingsTab}, setInfoProperty} = useContext(AppInfoContext)
    const {API, downloadReport} = useContext(ApiContext)
    const [selectedTab, setSelectedTab] = useState('') 
    const [reportData, setReportData] = useState<any>(null)
    const [report, setReport] = useState<Array<any>|null>(null)
    const [resetPagination, setResetPagination] = useState<boolean>(false)
    const [billingData, setBillingData] = useState({expenseGL: '', qoreAccount: ''})
    const [view, setView] = useState('')
    const [products, setProducts] = useState<Array<any>>([])
    const [billingConfig, setBillingConfig] = useState<any>(null)
    const [categoryStatus, setCategoryStatus] = useState('')

    const tableTabs = ['categories', 'products']

    const filterDataRef = useRef<any>({})

    useEffect(() => {
        if (selectedTab) {
            filterDataRef.current = {}
            if (tableTabs.includes(selectedTab)) handleFetchReport()
            if (selectedTab === 'billing') handleFetchBillingConfig()
        }
    }, [selectedTab])

    useEffect(() => {
        if (!selectedTab) setSelectedTab(selectedSettingsTab || 'products')
    }, [selectedSettingsTab])

    function handleSelectedTab (tab: string) {
        setSelectedTab(tab)
        setInfoProperty('selectedSettingsTab', tab)
    }

    const handleBillingData = (e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>) => setBillingData({...billingData, [e.target.name]: e.target.value})

    async function handleFetchReport () {
        if (selectedTab === 'categories') handleCategoriesReport()
        else handleFetchInstitutionProducts()
    }
    
    async function handleCategoriesReport (pageNumber = 1, pageSize = 10, isDownload = false) {
        const fetchPayload = { 
            pageNumber, pageSize, 
            sortOrder: 'ASC', sortColumn: 'name',
            ...filterDataRef.current, 
            filter: {...filterDataRef.current.filter}
        }
        
        const response = await API.getSettingsCategories(fetchPayload)

        if (response) {
            if (isDownload) {
                return downloadReport(response, 'CATEGORIES-SETTINGS_REPORT')
            }

            response.data = helpers.addSerialNumberToItems(response?.data, {pageNumber, pageSize})

            setReportData(response)
            setReport(response.data)
        }
    }

    function handleClear () {
        filterDataRef.current = {}
        handleFetchReport()
        setResetPagination(!resetPagination)
    }

    async function handleFetchInstitutionProducts () {
        const response = await API.getInstitutionApps({isActive: null})
        if (response) setProducts(response)
    }

    async function handleFetchBillingConfig () {
        const response = await API.getBillingConfiguration()

        if (response) {
            setBillingConfig(response)
            setBillingData({expenseGL: response.expenseGL, qoreAccount: response.qoreAccount})
        }
    }

    async function handleCategoryAction (id: string, status: string) {
        const response = await API.activateOrDeactivateCategory(id, status)
        if (response) {
            setCategoryStatus(response)
            handleCategoriesReport()
        }
    }

    async function handleProductAction (data: any, value: string) {
        if (value === 'view') {
            await setInfoProperty('selectedProduct', data?.product)
            navigate(`/client/discover/product/${data.product.id}`)
        }
        else {
            const response = value === 'activate' ? 
                await API.activateInstitutionProduct(data.product.id)
                : await API.deactivateInstitutionProduct(data.product.id)

            if (response) handleFetchInstitutionProducts()
        }
    }

    async function handleSubmit (e: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e.preventDefault()

        const response = billingConfig ?
            await API.updateConfiguredBilling({...billingData, id: billingConfig?.id}) 
            : await API.configureBilling(billingData)
    }

    return (
        <>
            <PageWrapper>
                <CustomContainer>
                    <PageTitle 
                        color='#101828' textSize='3' fontWeight="500"
                    >
                       Settings
                    </PageTitle>
                    <AppSpan>Manage your account settings and preferences here.</AppSpan>
                </CustomContainer>
                <CustomContainer topMargin="2.5">
                    <CustomContainer 
                        bottomMargin='2'
                        className='scrollable-container'
                    >
                        <FlexRow
                            gap='2' height='4' alignItems='flex-start'
                            className='tab-container'
                        >
                            {
                                tabsInfo.map((item, index) => {
                                    return (
                                        <AppSpan
                                            fontWeight={device.isFirefox ? '400' : '500'}
                                            cursor='pointer'
                                            color={selectedTab === item.value ? '#0A756F' : '#667085'}
                                            onClick={() => handleSelectedTab(item.value)}
                                            key={index}
                                    >
                                            {item.label}
                                        </AppSpan>
                                    )
                                })
                            }
                            <AbsoluteContainer 
                                bottom='0' 
                                left={selectedTab === 'products' ? '0' : selectedTab === 'categories' ? '8.2' : '17.7'}
                            >
                                <CustomContainer
                                    width={selectedTab === 'products' ? '6.5' : selectedTab === 'categories' ? '8' : '5'}
                                    height='0.2' bgColor='#0A756F'
                                />
                            </AbsoluteContainer>
                        </FlexRow>
                    </CustomContainer>

                    { selectedTab === 'categories' ? //tableTabs.includes(selectedTab) ?
                        <ServerSideTable
                            tableData={report}
                            tableColumns={categoriesSettingColumns(handleCategoryAction)} 
                            dataFetchMethod={handleCategoriesReport}
                            totalRows={reportData?.totalRecords}
                            customStyle
                            shouldResetPagination={resetPagination}
                            canFilter={() => setView('filter')}
                            canClear={handleClear}
                            downloadAction={() => handleCategoriesReport(1, 10, true)}
                        />
                        : selectedTab === 'products' ?
                        <Table 
                            tableData={products}
                            canFilter={() => setView('filter')}
                            canClear={handleClear}
                            tableColumns={productsSettingColumns(handleProductAction)} 
                            customStyle
                            downloadAction={() => downloadReport(products, 'PRODUCTS-SETTINGS_REPORT')}
                        />
                        :
                        <CustomContainer
                            minHeight="30.8" width="60.2" 
                            bgColor="#ffffff" topMargin="3"
                            radius="0.8" borderColor="#EAECF0"
                            box-shadow='0px 1px 2px 0px #1018280F'
                            padding="2"
                            className="billing-settings-container"
                        >
                            <AppSpan textSize="1.5">Easily customise your billing settings.</AppSpan>
                            <Form topMargin="2" onSubmit={handleSubmit}>
                                <FlexRow 
                                    justifyContent="space-between" 
                                    gap="1.5" wrap="wrap"
                                >
                                    <AppSpan color="#344054" fontWeight='500'>Expense GL</AppSpan>
                                    <FormGroup 
                                        style={{width: '30rem'}}
                                        bottomMargin="0"
                                    >
                                        <input 
                                            placeholder="Enter Expense GL" 
                                            name='expenseGL'
                                            value={billingData.expenseGL}
                                            onChange={handleBillingData}
                                            autoComplete='off'
                                            required
                                        />
                                    </FormGroup>
                                </FlexRow>
                                <FlexRow 
                                    justifyContent="space-between" 
                                    gap="1.5"  topMargin="2" wrap="wrap"
                                >
                                    <AppSpan color="#344054" fontWeight='500'>Appzone account number</AppSpan>
                                    <FormGroup 
                                        style={{width: '30rem'}} 
                                        bottomMargin="0"
                                    >
                                        <input 
                                            placeholder="Enter Appzone account number" 
                                            name='qoreAccount'
                                            value={billingData.qoreAccount}
                                            onChange={handleBillingData}
                                            autoComplete='off'
                                            required
                                        />
                                    </FormGroup>
                                </FlexRow>
                                {/* <FlexRow 
                                    justifyContent="space-between" 
                                    gap="1.5"  topMargin="2" wrap="wrap"
                                >
                                    <AppSpan color="#344054" fontWeight='500'>Others</AppSpan>
                                    <FormGroup 
                                        style={{width: '30rem'}}
                                        bottomMargin="0"
                                    >
                                        <input 
                                            placeholder="Enter Others" 
                                            name='others'
                                            value={billingData.others}
                                            onChange={handleBillingData}
                                            autoComplete='off'
                                        />
                                    </FormGroup>
                                </FlexRow> */}
                                <FlexRow justifyContent="flex-end" topMargin='2'>
                                    <Button
                                        width='6.7'
                                        fontWeight="500" 
                                        height='4' radius='0.8'
                                        hoverColor='#0D968F'
                                        hoverBgColor='#ffffff' borderColor='#0D968F'
                                    >
                                        Save
                                    </Button>
                                </FlexRow>
                            </Form>
                        </CustomContainer>
                    }
                </CustomContainer>
            </PageWrapper>
            { view === 'filter' &&
                <Filter
                    type={selectedTab === 'categories' ? 'category-setting' : 'product-setting'}
                    onApplyFilter={handleFetchReport}
                    setFilterData={(data: any) => {filterDataRef.current = data}}
                    close={() => setView('')}
                />
            }
        </>
    )
}

export default utility.routeData('settings', 'Settings', <Settings/>)

const tabsInfo = [
    {label: 'Products', value: 'products'},
    {label: 'Categories', value: 'categories'},
    {label: 'Billing', value: 'billing'}
]